const SUPPRESSED_TRAFFIC_TYPE = "internal";
const INTERNAL_HOST_EXACT_MATCHES = new Set(["localhost", "127.0.0.1", "::1"]);
const INTERNAL_HOST_SUFFIXES = [".vercel.app"];
const INTERNAL_HOST_PREFIXES = ["dev."];
const EXCLUDED_PATH_PREFIXES = [
  "/audio-test",
  "/chat-comparison",
  "/chat-tester",
  "/sales-sim",
  "/admin/superUser",
  "/admin/support",
];

function normalizePathname(pathname = "/") {
  if (!pathname || typeof pathname !== "string") {
    return "/";
  }

  return pathname.startsWith("/") ? pathname : `/${pathname}`;
}

function normalizeSearch(search = "") {
  if (!search || typeof search !== "string") {
    return "";
  }

  return search.startsWith("?") ? search : `?${search}`;
}

function normalizeReferrer(referrer = "") {
  if (!referrer || typeof referrer !== "string") {
    return "";
  }

  return referrer.trim();
}

function isInternalHost(hostname = "") {
  if (!hostname || typeof hostname !== "string") {
    return false;
  }

  const normalizedHostname = hostname.trim().toLowerCase();
  return (
    INTERNAL_HOST_EXACT_MATCHES.has(normalizedHostname) ||
    INTERNAL_HOST_PREFIXES.some((prefix) => normalizedHostname.startsWith(prefix)) ||
    INTERNAL_HOST_SUFFIXES.some((suffix) => normalizedHostname.endsWith(suffix))
  );
}

function isExcludedPath(pathname = "") {
  return EXCLUDED_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

function resolveAppSection(pathname = "") {
  if (pathname.startsWith("/simulator")) return "simulator";
  if (pathname.startsWith("/workspaces/admin")) return "admin";
  if (pathname.startsWith("/workspaces/reports")) return "reports";
  if (pathname.startsWith("/dashboard")) return "dashboard";
  if (pathname.startsWith("/training")) return "training";
  if (pathname.startsWith("/join") || pathname.startsWith("/oauth")) return "auth";
  if (pathname === "/") return "home";
  return "other";
}

export function getAnalyticsPageContext({
  pathname = "/",
  search = "",
  session = null,
  hostname = "",
  origin = "",
  referrer = "",
} = {}) {
  const normalizedPathname = normalizePathname(pathname);
  const normalizedSearch = normalizeSearch(search);
  const pagePath = `${normalizedPathname}${normalizedSearch}`;
  const pageLocation = origin ? `${origin}${pagePath}` : pagePath;
  const pageReferrer = normalizeReferrer(referrer);
  const emailDomain = session?.user?.email?.split("@")?.[1]?.toLowerCase() || "";
  const reasons = [];

  if (isInternalHost(hostname)) {
    reasons.push("internal_host");
  }

  if (isExcludedPath(normalizedPathname)) {
    reasons.push("internal_path");
  }

  if (emailDomain === "salesably.ai") {
    reasons.push("internal_user");
  }

  if (session?.user?.superUser) {
    reasons.push("super_user");
  }

  if (session?.user?.isImpersonated) {
    reasons.push("impersonated_session");
  }

  const shouldTrack = reasons.length === 0;
  const appSection = resolveAppSection(normalizedPathname);
  const trafficType = shouldTrack ? null : SUPPRESSED_TRAFFIC_TYPE;

  return {
    appSection,
    pageLocation,
    pagePath,
    pageReferrer,
    shouldTrack,
    suppressionReasons: reasons,
    trafficType,
    defaultEventParameters: {
      app_section: appSection,
      page_location: pageLocation,
      page_path: pagePath,
      ...(pageReferrer ? { page_referrer: pageReferrer } : {}),
      ...(trafficType ? { traffic_type: trafficType } : {}),
    },
  };
}
